import { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileText, Plus, Search, Upload, Edit, Play, Trash2 } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';
import { columns, Script } from './columns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';

export function ManageScripts() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedScript, setSelectedScript] = useState<Script | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Mock data - will be replaced with real data from API
  const scripts: Script[] = [
    {
      id: 'script-001',
      name: 'data_collection.py',
      description: 'Collects data from various sources',
      path: 'scripts/example/01_data_collection.py',
      lastModified: '2024-01-15T10:30:00Z',
      size: '2.4 KB',
      inputs: ['source_url', 'output_path'],
      outputs: ['cleaned_data.csv'],
      estimatedTime: '30-60 seconds',
      tags: ['data', 'collection', 'etl'],
    },
    {
      id: 'script-002',
      name: 'preprocessing.py',
      description: 'Preprocesses and cleans data',
      path: 'scripts/example/02_preprocessing.py',
      lastModified: '2024-01-15T09:15:00Z',
      size: '3.8 KB',
      inputs: ['input_data.csv', 'config.json'],
      outputs: ['processed_data.csv'],
      estimatedTime: '1-2 minutes',
      tags: ['preprocessing', 'cleaning', 'data'],
    },
    {
      id: 'script-003',
      name: 'analysis.py',
      description: 'Performs statistical analysis',
      path: 'scripts/example/03_analysis.py',
      lastModified: '2024-01-14T16:45:00Z',
      size: '5.2 KB',
      inputs: ['processed_data.csv', 'analysis_type'],
      outputs: ['analysis_results.json', 'charts/'],
      estimatedTime: '2-5 minutes',
      tags: ['analysis', 'statistics', 'visualization'],
    },
    {
      id: 'script-004',
      name: 'reporting.py',
      description: 'Generates final reports',
      path: 'scripts/example/04_reporting.py',
      lastModified: '2024-01-14T14:20:00Z',
      size: '4.1 KB',
      inputs: ['analysis_results.json', 'template.html'],
      outputs: ['final_report.pdf'],
      estimatedTime: '1-3 minutes',
      tags: ['reporting', 'pdf', 'export'],
    },
  ];

  const filteredScripts = scripts.filter(script =>
    script.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    script.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    script.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Transform data for DataTable
  const tableData: Script[] = filteredScripts;

  const handleViewDetails = (script: Script) => {
    setSelectedScript(script);
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Manage Scripts
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            View, edit, and organize your workflow scripts
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <Button variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Upload Script
          </Button>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Script
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search scripts by name, description, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Scripts Table */}
        <Card>
          <CardContent className="pt-6">
            <DataTable
              columns={columns}
              data={tableData}
              searchPlaceholder="Search scripts..."
              searchColumn="name"
              onRowClick={handleViewDetails}
            />
          </CardContent>
        </Card>
      </div>

      {/* Script Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>Script Details</span>
            </DialogTitle>
            <DialogDescription>
              Detailed information about the selected script
            </DialogDescription>
          </DialogHeader>
          
          {selectedScript && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Name</h4>
                  <p className="text-sm text-gray-600">{selectedScript.name}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Size</h4>
                  <p className="text-sm text-gray-600">{selectedScript.size}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Estimated Time</h4>
                  <p className="text-sm text-gray-600">{selectedScript.estimatedTime}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Last Modified</h4>
                  <p className="text-sm text-gray-600">
                    {new Date(selectedScript.lastModified).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-1">Description</h4>
                <p className="text-sm text-gray-600">{selectedScript.description}</p>
              </div>

              {/* Path */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-1">Path</h4>
                <p className="text-sm text-gray-600 font-mono bg-gray-100 p-2 rounded">
                  {selectedScript.path}
                </p>
              </div>

              {/* Inputs & Outputs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Inputs</h4>
                  <div className="space-y-1">
                    {selectedScript.inputs.map((input: string, index: number) => (
                      <div key={index} className="text-sm text-gray-600 bg-blue-50 p-2 rounded">
                        {input}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Outputs</h4>
                  <div className="space-y-1">
                    {selectedScript.outputs.map((output: string, index: number) => (
                      <div key={index} className="text-sm text-gray-600 bg-green-50 p-2 rounded">
                        {output}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedScript.tags.map((tag: string) => (
                    <Badge key={tag} variant="secondary">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2 pt-4">
                <Button className="flex-1">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Script
                </Button>
                <Button variant="outline" className="flex-1">
                  <Play className="h-4 w-4 mr-2" />
                  Test Run
                </Button>
                <Button variant="destructive" className="flex-1">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}